/** Detects when a stage's structured Rich*Body would render nothing
 *  substantive — used to decide whether the RawOutputFallback should open
 *  by default so the reviewer can see what the agent actually returned
 *  even when CMP's typed renderer didn't recognise the shape.
 *
 *  Each entry names the field(s) the matching Rich*Body keys off. When
 *  ALL of those fields are empty / falsy, we treat the rich render as
 *  empty and auto-open the raw output. */
import type { PausedStage } from '@/types/enums';

const hasItems = (v: unknown): boolean => Array.isArray(v) && v.length > 0;
const hasText = (v: unknown): boolean => typeof v === 'string' && v.trim().length > 0;

export function isRichRenderEmpty(stage: PausedStage, output: Record<string, unknown>): boolean {
  switch (stage) {
    case 'document_assistant':
      return !hasText(output.text) && !hasItems(output.handwritten_regions);
    case 'document':
      return !hasItems(output.sections) && !hasText(output.contract_summary);
    case 'parties':
      return !hasItems(output.parties);
    case 'clause':
      return !hasItems(output.clauses);
    case 'compliance':
      return (
        !hasItems(output.contractual_compliance_issues) &&
        !hasItems(output.missing_compliance_clauses) &&
        !hasText(output.compliance_summary)
      );
    case 'signature':
      return !hasItems(output.signature_blocks);
    case 'gate_1':
    case 'gate_2':
      return !hasText(output.decision) && !hasItems(output.critical_issues);
    case 'gate':
      return (
        !hasText(output.pipeline_verdict) &&
        !hasItems(output.key_findings) &&
        !hasText(output.executive_summary)
      );
    case 'orchestrator_bootstrap': {
      // Final client report: RichBootstrapBody renders any of the nine
      // structured sections, or falls back to the free-text narrative.
      const sectionKeys = [
        'executive_summary',
        'contract_overview',
        'key_findings',
        'compliance_status',
        'execution_status',
        'clause_analysis_summary',
        'missing_provisions',
        'recommendations',
        'pipeline_quality_note',
      ];
      const hasStructured = sectionKeys.some((k) => hasText(output[k]));
      return !hasStructured && !hasText(output.narrative);
    }
    case 'orchestrator':
      return !hasItems(output.planned_agents);
    default:
      return false;
  }
}
